
import { createFileLogger } from '../utils/logger';
import { tokenRefresher } from './tokenRefresher';

const log = createFileLogger('FETCH_SERVICE');

// Add a connection pool manager to limit concurrent requests
const FetchConnectionPool = {
  maxConcurrentRequests: 6, // Limit concurrent requests
  activeRequests: 0,
  pendingQueue: [] as (() => void)[],
  // Tracks AbortControllers for all in-flight requests so they can be cancelled on network crash
  activeControllers: new Set<AbortController>(),
  
  // Request a connection slot
  acquireConnection(): Promise<() => void> {
    return new Promise((resolve) => {
      // If we have capacity, grant immediately
      if (this.activeRequests < this.maxConcurrentRequests) {
        this.activeRequests++;
        // Return a release function
        resolve(() => {
          this.activeRequests--;
          this.processQueue();
        });
      } else {
        // Otherwise queue the request
        this.pendingQueue.push(() => {
          this.activeRequests++;
          // Return a release function
          resolve(() => {
            this.activeRequests--;
            this.processQueue();
          });
        });
      }
    });
  },
  
  // Process the pending queue when connections are released
  processQueue() {
    if (this.pendingQueue.length > 0 && this.activeRequests < this.maxConcurrentRequests) {
      const next = this.pendingQueue.shift();
      if (next) next();
    }
  }
};

/**
 * Abort all in-flight fetchService requests immediately.
 * Called when the Electron network service crashes so the pool drains
 * instantly instead of waiting for individual 60-second timeouts.
 */
export function abortAllPendingRequests(): void {
  if (FetchConnectionPool.activeControllers.size === 0) return;
  const log2 = createFileLogger('FETCH_SERVICE');
  log2.warn(`[POOL] abortAllPendingRequests: aborting ${FetchConnectionPool.activeControllers.size} in-flight requests`);
  for (const ctrl of FetchConnectionPool.activeControllers) {
    ctrl.abort();
  }
  FetchConnectionPool.activeControllers.clear();
}

// Listen for the Electron main-process crash signal (forwarded via preload) and
// drain the pool immediately — no polling required.
if (typeof window !== 'undefined') {
  window.addEventListener('network-service-crashed', () => {
    abortAllPendingRequests();
  });
}

// Extended RequestInit type to support streaming and custom timeout
interface FetchServiceInit extends RequestInit {
  stream?: boolean; // When true, returns raw Response for streaming
  timeoutMs?: number; // Optional per-request timeout override
}


// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 2,
  baseDelay: 500,
  maxDelay: 5000,
  // 500/502/503/504 are transient during Gunicorn worker cycling or gateway timeouts — always retry.
  // 429 (rate limited) is transient and should back off and retry.
  // 400/401/403/404/422 are deterministic client or auth errors — never retry.
  nonRetryableStatuses: [400, 401, 403, 404, 422],
  transientServerStatuses: [429, 500, 502, 503, 504],
};

// Faster retry delays: 500ms, 1000ms
function getRetryDelay(attempt: number): number {
  return RETRY_CONFIG.baseDelay * (attempt + 1);
}

/** Get auth token from localStorage - prefer authToken, fallback to id_token (used by custom/email auth) */
function getAuthToken(): string | null {
  return localStorage.getItem('authToken') || localStorage.getItem('id_token');
}

// Tracks in-flight GET requests so that two concurrent calls for the same URL
// (e.g. orgYDocStore + space_open_window both fetching a newly-created entity)
// share one HTTP request instead of firing two.
const _inflightGETs = new Map<string, Promise<any>>();

export default function fetchService(url: string, init?: FetchServiceInit, retryAttempt: number = 0): Promise<any> {
  const method = (init?.method || 'GET').toUpperCase();

  if (method === 'GET' && !init?.stream && retryAttempt === 0) {
    const existing = _inflightGETs.get(url);
    if (existing) return existing;
    const promise = _fetchImpl(url, init, retryAttempt);
    _inflightGETs.set(url, promise);
    promise.then(() => _inflightGETs.delete(url), () => _inflightGETs.delete(url));
    return promise;
  }

  return _fetchImpl(url, init, retryAttempt);
}

async function _fetchImpl(url: string, init?: FetchServiceInit, retryAttempt: number = 0): Promise<any> {
  const authToken = getAuthToken();
  
  // Debug logging for integration entity updates
  if (url.includes('/entities/') && init?.method === 'PUT') {
    log.log(`[FETCH_SERVICE] PUT request to ${url}`);
    log.log(`[FETCH_SERVICE] Auth token present: ${!!authToken}`);
    if (!authToken) {
      log.error(`[FETCH_SERVICE] ❌ NO AUTH TOKEN FOUND IN LOCALSTORAGE`);
      log.error(`[FETCH_SERVICE] localStorage keys:`, Object.keys(localStorage));
    }
  }
  
  // Enhanced auth debugging for entity permissions
  // if (url.includes('/entities/') && url.includes('/permissions')) {
  //   log.log(`[AUTH_DEBUG] Making request to entity permissions endpoint: ${url}`);
  //   log.log(`[AUTH_DEBUG] Has auth token: ${!!authToken}`);
  //   if (authToken) {
  //     log.log(`[AUTH_DEBUG] Token length: ${authToken.length}`);
  //     log.log(`[AUTH_DEBUG] Token preview: ${authToken.substring(0, 20)}...`);
  //   }
  // }
  
  // Log retry attempts
  if (retryAttempt > 0) {
    log.info(`[RETRY] Making retry attempt ${retryAttempt} for ${url}`);
  }
  
  // Acquire a connection from the pool
  let releaseConnection: (() => void) | null = null;
  
  try {
    // Wait for an available connection slot
    releaseConnection = await FetchConnectionPool.acquireConnection();
    
    // log.log(`Making ${init?.method || 'GET'} request to: ${url}`);
    
    // Log detailed request information for debugging
    // log.log('[fetchService] Request details:', {
    //   method: init?.method || 'GET',
    //   url,
    //   headers: init?.headers,
    //   hasAuthToken: !!authToken,
    //   mode: init?.mode || 'cors',
    //   credentials: init?.credentials || 'include',
    //   isStreaming: !!init?.stream
    // });
    
    // Add a timeout to prevent hanging requests
    // Use longer timeout for file uploads or known long-running Claude endpoints
    const isLongRunningClaudeEndpoint = url.includes('/claude/websearch') || url.includes('/claude/chat');
    const isFileUpload = url.includes('/files/add') || url.includes('/files/upload');
    const timeoutMs = (init?.timeoutMs != null)
      ? init.timeoutMs
      : (isLongRunningClaudeEndpoint ? 900000 : isFileUpload ? 300000 : 60000); // 15 min for Claude/wizards, 5 min for file uploads, 1 min for others
    
    // Note: /claude/websearch/stream uses fetch() directly with streaming reader in aiCommands.ts, not fetchService
    const controller = new AbortController();
    FetchConnectionPool.activeControllers.add(controller);
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs); // 60 second timeout for AI responses
    
    try {
      const response = await fetch(url, {
        ...init,
        headers: {
          // Only add Content-Type for non-FormData requests that have a body
          ...(init?.body && !(init.body instanceof FormData) 
            ? { 'Content-Type': 'application/json' } 
            : {}),
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          ...init?.headers,
        },
        // Always include credentials to enable cookies/auth
        credentials: 'include',
        // Explicitly set cors mode
        mode: 'cors',
        signal: controller.signal,
        // Prevent browser from keeping connections alive too long
        cache: 'no-store',
        keepalive: false
      });
      
      // Clear the timeout and unregister controller
      clearTimeout(timeoutId);
      FetchConnectionPool.activeControllers.delete(controller);

      // Log detailed response information for debugging
      // log.log('[fetchService] Response details:', {
      //   url,
      //   status: response.status,
      //   statusText: response.statusText,
      //   contentType: response.headers.get('Content-Type'),
      //   isStreaming: !!init?.stream,
      //   headers: Array.from(response.headers.entries()).reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
      // });

      if (!response.ok) {
        // Create a custom error with the response attached
        const error: any = new Error(`Request failed with status: ${response.status}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.response = response.clone(); // Clone so we can still read it later

        // First try to get error as JSON
        try {
          const contentType = response.headers.get('Content-Type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            error.data = errorData;
            
            // Support both "error" and "detail" fields (FastAPI uses "detail")
            if (errorData.error) {
              error.message = `${error.message}. ${errorData.error}`;
              if (errorData.details) {
                error.message += `: ${errorData.details}`;
              }
            } else if (errorData.detail) {
              // FastAPI standard error format
              error.message = `${error.message}. ${errorData.detail}`;
            }
          } else {
            // If not JSON, use text
            const errorText = await response.text();
            if (errorText) {
              error.data = errorText;
              error.message = `${error.message}. ${errorText}`;
            }
          }
          
          if (response.status === 401) {
            log.error('Authentication error (401 Unauthorized):', error.message);
            
            const currentAuthToken = getAuthToken();
            const wasAuthenticatedForRequest = !!authToken;
            const tokenChanged = authToken && currentAuthToken && authToken !== currentAuthToken;
            
            if (wasAuthenticatedForRequest && tokenChanged) {
              log.info('[AUTH] 401 with old token, but token was already refreshed — retrying with new token');
              throw Object.assign(error, { _authRetryable: true });
            } else if (wasAuthenticatedForRequest && !tokenChanged) {
              log.warn('[AUTH] 401 received — attempting silent refresh');
              const newToken = await tokenRefresher.tryRefresh();
              if (newToken) {
                log.log('[AUTH] Silent refresh succeeded after 401 — retrying request');
                throw Object.assign(error, { _authRetryable: true });
              }
              log.warn('[AUTH] Silent refresh failed — triggering session expired modal');
              import('./sessionManager').then(({ sessionManager }) => {
                sessionManager.handleSessionExpired({
                  message: 'Your session has expired. Please log in again to continue.'
                });
              });
            } else {
              log.info('[AUTH] 401 on unauthenticated request (expected for public document viewing)');
            }
          }
          
          // Handle authorization errors specifically  
          if (response.status === 403) {
            log.error('Authorization error (403 Forbidden):', error.message);
            log.error('[AUTH_DEBUG] 403 error details:', {
              url,
              hasAuthToken: !!getAuthToken(),
              tokenPreview: getAuthToken()?.substring(0, 20) + '...'
            });
            // User is authenticated but doesn't have permission for this resource
          }
          
          // Handle 404 errors specifically for entity permissions
          if (response.status === 404 && url.includes('/entities/') && url.includes('/permissions')) {
            log.error('[AUTH_DEBUG] 404 error on entity permissions endpoint:', {
              url,
              hasAuthToken: !!getAuthToken(),
              errorMessage: error.message,
              responseData: error.data
            });
          }
          
          // Handle server errors with data corruption context
          if (response.status === 500) {
            log.error('[DATA_CORRUPTION_DETECTION] Server error:', error.message);
            
            // Check if it's likely a data serialization issue
            if (error.message.includes('not JSON serializable') || 
                error.message.includes('ObjectId')) {
              log.error('[DATA_CORRUPTION_DETECTION] Detected ObjectId serialization issue in backend');
              error.message = `Data corruption detected: ${error.message}. Please contact support to fix corrupted entities.`;
              error.isDataCorruption = true;
            }
          }
          
        } catch (e) {
          if (e && typeof e === 'object' && (e as any)._authRetryable) throw e;
          log.warn('Error parsing response body:', e);
        }
        
        throw error;
      }

      // ✅ NEW: For streaming requests, return raw Response object
      if (init?.stream) {
        log.log(`[fetchService] Returning raw Response for streaming: ${url}`);
        return response;
      }

      const contentType = response.headers.get('Content-Type');

      if (contentType?.includes('application/json')) {
        try {
          const jsonResponse = await response.json();
          // log.log(`JSON response from ${url}:`, jsonResponse);
          return jsonResponse;
        } catch (error: unknown) {
          const jsonError = error instanceof Error ? error : new Error(String(error));
          log.error(`Error parsing JSON from ${url}:`, jsonError);
          throw new Error(`Failed to parse JSON response: ${jsonError.message}`);
        }
      }

      // Handle binary files and files that should be returned as byte arrays
      if (contentType?.includes('application/octet-stream') || 
          contentType?.toLowerCase()?.includes('pdf') ||
          contentType?.toLowerCase()?.includes('image/') ||
          contentType?.toLowerCase()?.includes('application/x-python') ||
          contentType?.toLowerCase()?.includes('text/x-python')) {
        const arrayBuffer = await response.arrayBuffer();
        return new Uint8Array(arrayBuffer);
      }

      // For URLs ending with specific file extensions that should be treated as text
      // Check if the URL ends with a known code file extension
      const codeFileExtensions = ['.py', '.js', '.jsx', '.ts', '.tsx', '.html', '.css', '.json', '.md', '.txt', '.csv'];
      const isCodeFile = codeFileExtensions.some(ext => url.toLowerCase().endsWith(ext));

      if (isCodeFile) {
        // For code files, we still want to return the raw ArrayBuffer to ensure consistent handling
        const arrayBuffer = await response.arrayBuffer();
        return new Uint8Array(arrayBuffer);
      }

      // For other content types, return as text
      const textResponse = await response.text();
      log.log(`Text response from ${url}:`, textResponse);
      return textResponse;
    } catch (error: unknown) {
      // Clear the timeout and unregister controller
      clearTimeout(timeoutId);
      FetchConnectionPool.activeControllers.delete(controller);
      
      // Check for AbortError (timeout)
      if (error instanceof Error && error.name === 'AbortError') {
        const timeoutSeconds = Math.round(timeoutMs / 1000);
        log.error(`Request timeout for ${url} (exceeded ${timeoutSeconds} seconds)`);
        throw new Error(`Request timeout: The server took too long to respond (${timeoutSeconds}s limit)`);
      }

      // 401 auth retry: token was refreshed (either mid-flight or by silent refresh).
      // Immediately retry the original request with the fresh token — no delay needed.
      if (error && typeof error === 'object' && (error as any)._authRetryable && retryAttempt === 0) {
        log.log(`[AUTH_RETRY] Retrying ${url} with refreshed token`);
        return fetchService(url, init, retryAttempt + 1);
      }
      
      // Handle retry logic
      const shouldRetry = retryAttempt < RETRY_CONFIG.maxRetries;

      // Network-level errors (connection refused, DNS failure, dropped TCP)
      const isRetryableError = error instanceof Error && 
        (error.message.includes('Failed to fetch') || 
         error.message.includes('NetworkError') ||
         error.message.includes('ECONNRESET') ||
         error.message.includes('ENOTFOUND'));
      
      // HTTP status-based retry: only retry known transient server errors (worker cycling, deploys).
      // All other statuses fail immediately — unknown 4xx codes must not be retried.
      let shouldRetryStatus = false;
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as any).status;
        shouldRetryStatus = RETRY_CONFIG.transientServerStatuses.includes(status);
      }
      
      if (shouldRetry && (isRetryableError || shouldRetryStatus)) {
        const delay = getRetryDelay(retryAttempt);
        log.warn(`[RETRY] Attempt ${retryAttempt + 1}/${RETRY_CONFIG.maxRetries} failed for ${url}. Retrying in ${delay}ms...`);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Retry the request - this will either resolve with success or reject with final error
        return fetchService(url, init, retryAttempt + 1);
      }
      
      // Log the final error
      if (retryAttempt > 0) {
        log.error(`[RETRY] All ${retryAttempt + 1} attempts failed for ${url}:`, error);
      } else {
        log.error(`Fetch error for ${url}:`, error);
      }
      
      throw error;
    }
  } finally {
    // Always release the connection back to the pool
    if (releaseConnection) {
      releaseConnection();
    }
  }
}
